import React, { useState, useEffect, useId, useMemo } from 'react';
import { Button, FormDateInput, FormTimeInput } from './index';

interface DateTimePickerProps {
    startDateTime: string;
    endDateTime: string;
    onDateRangeChange: (start: string, end: string) => void;
    className?: string;
    /** If true (default), emits on change without needing Apply */
    autoApply?: boolean;
    /** Debounce in ms for auto-apply (default 300ms) */
    debounceMs?: number;
}

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_INPUT_PATTERN = /^\d{2}:\d{2}$/;

type DateRangeValidation = {
    error: string | null;
    start: string | null;
    end: string | null;
};

const buildLocalDateTime = (dateValue: string, timeValue: string): Date | null => {
    if (!DATE_INPUT_PATTERN.test(dateValue) || !TIME_INPUT_PATTERN.test(timeValue)) {
        return null;
    }

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);

    if (
        Number.isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        date.getHours() !== hours ||
        date.getMinutes() !== minutes
    ) {
        return null;
    }

    return date;
};

const parseDateTimeValue = (dateString: string): Date | null => {
    if (!dateString) {
        return null;
    }

    if (dateString.includes('T')) {
        const isoDate = new Date(dateString);

        return Number.isNaN(isoDate.getTime()) ? null : isoDate;
    }

    const [datePart, rawTimePart = '00:00:00'] = dateString.trim().split(' ');
    const normalizedTimePart = rawTimePart.length === 5 ? `${rawTimePart}:00` : rawTimePart;
    const [hours = '00', minutes = '00'] = normalizedTimePart.split(':');

    return buildLocalDateTime(datePart, `${hours}:${minutes}`);
};

const formatDateForInput = (dateString: string) => {
    const date = parseDateTimeValue(dateString);

    if (!date) {
        return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const formatTimeForInput = (dateString: string) => {
    const date = parseDateTimeValue(dateString);

    if (!date) {
        return '';
    }

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
};

const formatToDateTimeString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const validateDateRange = (args: {
    endDateValue: string;
    endTimeValue: string;
    startDateValue: string;
    startTimeValue: string;
}): DateRangeValidation => {
    const { endDateValue, endTimeValue, startDateValue, startTimeValue } = args;
    const hasEmptyField = !startDateValue || !startTimeValue || !endDateValue || !endTimeValue;

    if (hasEmptyField) {
        return {
            error: 'Complete all date and time fields before applying.',
            start: null,
            end: null
        };
    }

    const resolvedStartDateTime = buildLocalDateTime(startDateValue, startTimeValue);
    const resolvedEndDateTime = buildLocalDateTime(endDateValue, endTimeValue);

    if (!resolvedStartDateTime || !resolvedEndDateTime) {
        return {
            error: 'Enter a valid date and time range.',
            start: null,
            end: null
        };
    }

    if (resolvedEndDateTime < resolvedStartDateTime) {
        return {
            error: 'End must be after start.',
            start: null,
            end: null
        };
    }

    return {
        error: null,
        start: formatToDateTimeString(resolvedStartDateTime),
        end: formatToDateTimeString(resolvedEndDateTime)
    };
};

const getManualHelperText = (args: { autoApply: boolean; hasPendingChanges: boolean; validation: DateRangeValidation }): string | null => {
    const { autoApply, hasPendingChanges, validation } = args;

    if (autoApply) {
        return null;
    }

    if (!hasPendingChanges) {
        return 'Edit the range, then apply to refresh the chart.';
    }

    return validation.error ?? 'Apply to refresh the chart with this range.';
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
    startDateTime,
    endDateTime,
    onDateRangeChange,
    className = '',
    autoApply = true,
    debounceMs = 300
}) => {
    const inputIdPrefix = useId();
    const appliedStartDateValue = useMemo(() => formatDateForInput(startDateTime), [startDateTime]);
    const appliedStartTimeValue = useMemo(() => formatTimeForInput(startDateTime), [startDateTime]);
    const appliedEndDateValue = useMemo(() => formatDateForInput(endDateTime), [endDateTime]);
    const appliedEndTimeValue = useMemo(() => formatTimeForInput(endDateTime), [endDateTime]);

    const [startDateValue, setStartDateValue] = useState(appliedStartDateValue);
    const [startTimeValue, setStartTimeValue] = useState(appliedStartTimeValue);
    const [endDateValue, setEndDateValue] = useState(appliedEndDateValue);
    const [endTimeValue, setEndTimeValue] = useState(appliedEndTimeValue);

    useEffect(() => {
        setStartDateValue(appliedStartDateValue);
        setStartTimeValue(appliedStartTimeValue);
        setEndDateValue(appliedEndDateValue);
        setEndTimeValue(appliedEndTimeValue);
    }, [appliedEndDateValue, appliedEndTimeValue, appliedStartDateValue, appliedStartTimeValue]);

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setStartDateValue(newDate);
    };

    const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = e.target.value;
        setStartTimeValue(newTime);
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setEndDateValue(newDate);
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = e.target.value;
        setEndTimeValue(newTime);
    };

    const hasPendingChanges =
        startDateValue !== appliedStartDateValue ||
        startTimeValue !== appliedStartTimeValue ||
        endDateValue !== appliedEndDateValue ||
        endTimeValue !== appliedEndTimeValue;

    const validation = useMemo(
        () =>
            validateDateRange({
                endDateValue,
                endTimeValue,
                startDateValue,
                startTimeValue
            }),
        [endDateValue, endTimeValue, startDateValue, startTimeValue]
    );

    const handleApplyCustomRange = () => {
        if (!validation.start || !validation.end || validation.error) {
            return;
        }

        onDateRangeChange(validation.start, validation.end);
    };

    const handleResetDraft = () => {
        setStartDateValue(appliedStartDateValue);
        setStartTimeValue(appliedStartTimeValue);
        setEndDateValue(appliedEndDateValue);
        setEndTimeValue(appliedEndTimeValue);
    };

    const handleManualApplyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (autoApply || event.key !== 'Enter') {
            return;
        }

        if (!validation.start || !validation.end || validation.error || !hasPendingChanges) {
            return;
        }

        event.preventDefault();
        handleApplyCustomRange();
    };

    // Auto-apply on changes with debounce
    useEffect(() => {
        if (!autoApply || !hasPendingChanges || !validation.start || !validation.end || validation.error) {
            return;
        }

        const nextStart = validation.start;
        const nextEnd = validation.end;

        const t = setTimeout(() => {
            onDateRangeChange(nextStart, nextEnd);
        }, debounceMs);

        return () => clearTimeout(t);
    }, [autoApply, debounceMs, hasPendingChanges, onDateRangeChange, validation.end, validation.error, validation.start]);

    const helperText = getManualHelperText({ autoApply, hasPendingChanges, validation });

    const helperTextClassName = validation.error && hasPendingChanges ? 'text-sm text-amber-400' : 'text-sm text-zinc-500';
    const startDateId = `${inputIdPrefix}-start-date`;
    const startTimeId = `${inputIdPrefix}-start-time`;
    const endDateId = `${inputIdPrefix}-end-date`;
    const endTimeId = `${inputIdPrefix}-end-time`;

    return (
        <div className={`flex flex-col gap-2.5 ${className}`}>
            <div className="flex flex-row items-center flex-wrap gap-2.5 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                    <label className="text-zinc-300 text-sm whitespace-nowrap m-0 flex items-center h-[38px]" htmlFor={startDateId}>
                        Start:
                    </label>
                    <div className="flex items-center">
                        <FormDateInput
                            noMargin
                            fullWidth={false}
                            id={startDateId}
                            value={startDateValue}
                            onChange={handleStartDateChange}
                            onKeyDown={handleManualApplyKeyDown}
                        />
                    </div>
                    <div className="flex items-center">
                        <FormTimeInput
                            noMargin
                            fullWidth={false}
                            id={startTimeId}
                            step={60}
                            value={startTimeValue}
                            onChange={handleStartTimeChange}
                            onKeyDown={handleManualApplyKeyDown}
                        />
                    </div>
                </div>
                <div className="flex items-center">
                    <label className="text-zinc-300 text-sm whitespace-nowrap m-0 flex items-center h-[38px]" htmlFor={endDateId}>
                        End:
                    </label>
                </div>
                <div className="flex items-center">
                    <FormDateInput
                        noMargin
                        fullWidth={false}
                        id={endDateId}
                        value={endDateValue}
                        onChange={handleEndDateChange}
                        onKeyDown={handleManualApplyKeyDown}
                    />
                </div>
                <div className="flex items-center">
                    <FormTimeInput
                        noMargin
                        fullWidth={false}
                        id={endTimeId}
                        step={60}
                        value={endTimeValue}
                        onChange={handleEndTimeChange}
                        onKeyDown={handleManualApplyKeyDown}
                    />
                </div>
                {!autoApply && (
                    <div className="flex items-center gap-2">
                        <Button disabled={!hasPendingChanges} size="md" variant="outline-dark" onClick={handleResetDraft}>
                            Reset
                        </Button>
                        <Button
                            disabled={!hasPendingChanges || Boolean(validation.error) || !validation.start || !validation.end}
                            size="md"
                            variant="primary"
                            onClick={handleApplyCustomRange}
                        >
                            Apply
                        </Button>
                    </div>
                )}
            </div>
            {helperText && (
                <div aria-live="polite" className={helperTextClassName}>
                    {helperText}
                </div>
            )}
        </div>
    );
};

export default DateTimePicker;
